import { useState } from "react";
import { Button } from "@hub/ui/components/ui/button";
import { Input } from "@hub/ui/components/ui/input";
import { Label } from "@hub/ui/components/ui/label";
import { ArrowLeft } from "lucide-react";

interface PasswordStepProps {
  email: string;
  displayName: string;
  onSubmit: (password: string) => void | Promise<void>;
  onBack: () => void;
  loading: boolean;
  error: string | null;
}

export function PasswordStep({
  email,
  displayName,
  onSubmit,
  onBack,
  loading,
  error,
}: PasswordStepProps) {
  const [password, setPassword] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password) {
      try {
        await onSubmit(password);
        setPassword(""); // Clear password from memory after successful submit
      } catch {
        // Password stays populated so the user can retry
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-3 border border-red-200 dark:border-red-800/50">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <div>
        <p className="text-lg font-medium text-gray-900 dark:text-white">
          Welcome back, {displayName}
        </p>
        <Button
          type="button"
          variant="link"
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mt-1 p-0 h-auto"
        >
          <ArrowLeft className="h-3 w-3" />
          {email}
        </Button>
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          required
        />
      </div>

      <Button type="submit" variant="primary" className="w-full" loading={loading}>
        Sign In
      </Button>

      {/* TODO: Add forgot password functionality when auth service supports password reset
      <div className="text-center">
        <button
          type="button"
          className="text-sm text-coronis-blue-600 hover:text-coronis-blue-700 dark:text-coronis-blue-400"
        >
          Forgot password?
        </button>
      </div>
      */}
    </form>
  );
}
